import React, { useEffect, useMemo, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { getByFilters, TOXICANTS_DISEASE } from '../../../api/Compounds';

// Define the interface for Toxicants_Disease documents
interface ToxicantsDisease {
    INCHIKEY: string;
    DISEASE_CATEGORY: string;
    COMMENT: string;
    LINK: string;
}

interface DiseaseCategorySelectorProps {
    selectedDiseaseCategory: string;
    setSelectedDiseaseCategory: (value: string) => void;
    setDiseaseInchiKeys: (inchiKeys: string[]) => void;
}

const DiseaseCategorySelector: React.FC<DiseaseCategorySelectorProps> = ({
                                                                             selectedDiseaseCategory,
                                                                             setSelectedDiseaseCategory,
                                                                             setDiseaseInchiKeys
                                                                         }) => {
    const [diseaseCategories, setDiseaseCategories] = useState<string[]>([]);

    // Fetch unique DISEASE_CATEGORY values from Toxicants_Disease
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                // Fetch all Toxicants_Disease documents to extract unique DISEASE_CATEGORY
                const response: ToxicantsDisease[] = await getByFilters(TOXICANTS_DISEASE, []);
                const categories = Array.from(
                    new Set(response.map(doc => doc.DISEASE_CATEGORY).filter(cat => cat))
                ).sort();
                setDiseaseCategories(['All', ...categories]);
            } catch (error) {
                console.error('Failed to fetch disease categories:', error);
                setDiseaseCategories(['All']);
            }
        };
        fetchCategories();
    }, []);

    // Fetch INCHIKEYs for the selected DISEASE_CATEGORY
    useEffect(() => {
        const fetchInchiKeys = async () => {
            if (selectedDiseaseCategory && selectedDiseaseCategory !== 'All') {
                try {
                    const response: ToxicantsDisease[] = await getByFilters(TOXICANTS_DISEASE, [
                        { property: 'DISEASE_CATEGORY', value: selectedDiseaseCategory, operator: '==' }
                    ]);
                    const inchiKeys = response
                        .map(doc => doc.INCHIKEY)
                        .filter(key => key);
                    setDiseaseInchiKeys(inchiKeys);
                } catch (error) {
                    console.error('Failed to fetch INCHIKEYs for disease category:', error);
                    setDiseaseInchiKeys([]);
                }
            } else {
                setDiseaseInchiKeys([]);
            }
        };
        fetchInchiKeys();
    }, [selectedDiseaseCategory, setDiseaseInchiKeys]);

    const handleDiseaseCategoryChange = (event: SelectChangeEvent<string>) => {
        setSelectedDiseaseCategory(event.target.value);
    };

    return (
        <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Disease Category</InputLabel>
            <Select
                value={selectedDiseaseCategory}
                onChange={handleDiseaseCategoryChange}
                label="Disease Category"
                size="small"
            >
                {diseaseCategories.map(category => (
                    <MenuItem key={category} value={category}>
                        {category}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default DiseaseCategorySelector;
